import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { resolveImg } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const BLOG_BG = "https://images.unsplash.com/photo-1765861625993-143a2a9c37c3?w=1920&q=80";

export default function BlogPage() {
    const [posts, setPosts] = useState([]);
    const [sel, setSel] = useState(null);
    const settings = useSettings();
    useEffect(() => {
        api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
    }, []);

    return (
        <>
        <title>Блог | AutoTreider</title>
        <meta name="description" content={`${settings?.blog_title || "Новости и статьи"} об импорте автомобилей из Европы.`} />
        <link rel="canonical" href="https://autotreider.eu/blog" />
        <meta property="og:title" content={`${settings?.blog_title || "Блог"} | ${settings?.company_name || "AutoTreider"}`} />
        <meta property="og:description" content={`${settings?.blog_title || "Новости и статьи"} об импорте автомобилей из Европы.`} />
        <meta property="og:url" content="https://autotreider.eu/blog" />
        <div className="min-h-screen pt-16 sm:pt-20" data-testid="blog-page">
            <section className="relative h-[30vh] sm:h-[40vh] flex items-center">
                <div className="absolute inset-0"><img src={BLOG_BG} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/70" /></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                    <p className="text-[#0099FF] text-xs uppercase tracking-[0.3em] mb-2 sm:mb-3 font-body">{settings?.blog_subtitle || "Блог"}</p>
                    <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white font-display">{settings?.blog_title || "Новости и статьи"}</h1>
                </div>
            </section>
            <section className="py-12 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                    {posts.length === 0 ? <p className="text-zinc-500 text-center py-12 sm:py-20 font-body">Статей пока нет</p> : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {posts.map((p, i) => (
                                <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="group bg-[#09090b] border border-white/5 hover:border-[#0099FF]/50 transition-all overflow-hidden cursor-pointer" onClick={() => setSel(p)} data-testid={`blog-card-${p.id}`}>
                                    {p.cover_image && <div className="aspect-[16/10] overflow-hidden"><img src={resolveImg(p.cover_image)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>}
                                    <div className="p-6">
                                        <div className="flex items-center gap-2 text-zinc-500 text-xs mb-3 font-body"><Calendar className="w-3 h-3" />{p.date}</div>
                                        <h3 className="text-white font-semibold mb-2 font-body">{p.title}</h3>
                                        <p className="text-zinc-500 text-sm line-clamp-3 font-body">{p.content?.slice(0, 150)}...</p>
                                        <div className="mt-4 flex items-center gap-1 text-[#0099FF] text-sm group-hover:text-white transition-colors font-body">Читать <ArrowRight className="w-4 h-4" /></div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
                <DialogContent className="bg-[#09090b] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="blog-post-modal">
                    <DialogHeader><DialogTitle className="text-white text-xl font-display">{sel?.title}</DialogTitle></DialogHeader>
                    {sel?.cover_image && <img src={resolveImg(sel.cover_image)} alt={sel.title} className="w-full aspect-video object-cover mb-4" />}
                    <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap font-body">{sel?.content}</div>
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
